import { DateTime } from "luxon";
import { useCallback, useEffect, useRef, useState } from "react";
import DatePicker from "react-datepicker";

import "react-datepicker/dist/react-datepicker.css";
import { ColorSelector } from "./ColorSelector";
import "./customDatePickerWidth.css";
import Button from "../components/Button";
import BulkActionDropdown from "../components/BulkActionDropdown";
import BulkActionModalHost from "../components/BulkActionModalHost";
import ModalPortal from "./ModalPortal";
import ConfirmModal from "./ConfirmModal";
import DeleteIconButton from "../components/DeleteIconButton";
import AddIconButton from "../components/AddIconButton";

const BULK_ACTIONS = [
	{
		id: "delete",
		label: "Delete",
		requiresMinSelection: 2,
		modal: "DELETE_CONFIRM"
	},
	{
		id: "updateColors",
		label: "Update Color",
		requiresMinSelection: 2,
		modal: "UPDATE_COLORS"
	},
	{
		id: "combineRanges",
		label: "Combine Date Ranges",
		requiresMinSelection: 2,
		modal: "COMBINE_RANGES"
	},
	{
		id: "sort",
		label: "Sort",
		requiresMinSelection: 2,
		modal: "SORT_CONFIRM"
	}
];

const mergeGroup = (group) => {
	const sorted = [...group].sort(
		(a, b) => a.startDate - b.startDate
	);

	const startDate = sorted[0].startDate;
	const endDate = sorted[sorted.length - 1].endDate;

	// colors: already resolved in the modal
	const colors = sorted[0].colors;

	const note = sorted
		.map(item => item.note)
		.filter(Boolean)
		.join(", ");

	return {
		...sorted[0],
		id: crypto.randomUUID(),
		startDate,
		endDate,
		colors,
		note,
		selected: false
	};
};

const naturalStringCompare = new Intl.Collator(undefined, {
	numeric: true,
	sensitivity: "base"
}).compare;

const compareValues = (a, b, field) => {
	switch (field) {
		case "startDate":
			return a.startDate - b.startDate;
		case "endDate":
			return a.endDate - b.endDate;
		case "duration":
			return (a.endDate - a.startDate) - (b.endDate - b.startDate);
		case "background":
			return (a.colors.background || "").localeCompare(b.colors.background || "");
		case "border":
			return (a.colors.border || "").localeCompare(b.colors.border || "");
		case "text":
			return (a.colors.text || "").localeCompare(b.colors.text || "");
		case "note":
			return naturalStringCompare(a.note || "", b.note || "");
		default:
			return 0;
	}
};

const sortDates = (dates, sortOptions) => {
	const sorted = [...dates];

	sorted.sort((a, b) => {
		for (const option of sortOptions) {
			const diff = compareValues(a, b, option.field);
			if (diff !== 0) {
				return option.direction === "ASC" ? diff : -diff;
			}
		}
		return 0;
	});

	return sorted;
};

const SelectDates = (props) => {
	const {
		closeModal,
		importHolidays,
		importDates,
		exportDates,
		refreshPreview,
		data,
		colors,
		colorOptions
	} = props;

	const [dates, setDates] = useState(
		data.map((d) => ({ ...d, selected: false }))
	);
	const [scrollToButton, setScrollToButton] = useState(false);

	const addButtonWrapperRef = useRef(null);
	const selectAllRef = useRef(null);

	const save = useCallback(() => {
		refreshPreview(dates);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [/*refreshPreview causes cycle!*/ dates]);

	// This makes the preview reload as soon as we change the color in the dialog!
	useEffect(save, [save, dates]);

	const saveAndClose = () => {
		save();
		closeModal();
	};

	const onImportHolidaysDates = () => {
		save();
		importHolidays();
	};

	const onImportDates = () => {
		save();
		importDates();
	};

	const onExportDates = () => {
		save();
		exportDates(dates);
	};

	const addDate = useCallback(() => {
		const newDates = [...dates];

		let nextDay = new Date();
		let newColors = { background: null, border: null, text: "#000000" };

		if (dates.length > 0) {
			const lastDate = DateTime.fromJSDate(dates[dates.length - 1].endDate);
			nextDay = lastDate.plus({ day: 1 }).toJSDate();
			newColors = { ...dates[dates.length - 1].colors };
		}

		newDates.push({
			id: crypto.randomUUID(),
			selected: false,
			dateValid: true,
			startDate: nextDay,
			endDate: nextDay,
			colors: newColors,
			note: ""
		});

		setDates(newDates);
		setScrollToButton(true);
	}, [dates]);

	useEffect(() => {
		if (!scrollToButton) {
			return;
		}
		setScrollToButton(false);
		addButtonWrapperRef.current?.scrollIntoView();
	}, [scrollToButton]);

	const updateSelectedColor = useCallback(
		(index, type, color) => {
			const newDates = [...dates];
			newDates[index].colors[type] = color;
			setDates(newDates);
		},
		[dates]
	);

	const updateStartDate = useCallback(
		(index, date) => {
			const newDates = [...dates];
			newDates[index].startDate = date;
			if (date > newDates[index].endDate) {
				newDates[index].endDate = date;
			}
			setDates(newDates);
		},
		[dates]
	);

	const updateEndDate = useCallback(
		(index, date) => {
			const newDates = [...dates];
			newDates[index].endDate =
				date >= newDates[index].startDate
					? date
					: newDates[index].startDate;
			setDates(newDates);
		},
		[dates]
	);

	const updateNote = useCallback(
		(index, note) => {
			const newDates = [...dates];
			newDates[index].note = note;
			setDates(newDates);
		},
		[dates]
	);

	const toggleRowSelected = useCallback(
		(index) => {
			const newDates = [...dates];
			newDates[index].selected = !newDates[index].selected;
			setDates(newDates);
		},
		[dates]
	);

	const selectedCount = dates.filter((d) => d.selected).length;

	const toggleSelectAll = useCallback(() => {
		const allSelected = dates.every((d) => d.selected);
		setDates(dates.map((d) => ({ ...d, selected: !allSelected })));
	}, [dates]);

	useEffect(() => {
		if (!selectAllRef.current) {
			return;
		}
		const selectedCount = dates.filter((d) => d.selected).length;
		selectAllRef.current.indeterminate =
			selectedCount > 0 && selectedCount < dates.length;
	}, [dates]);

	const [activeBulkAction, setActiveBulkAction] = useState(null);
	const [pendingSingleDeleteIndex, setPendingSingleDeleteIndex] = useState(null);

	const requestSingleDelete = useCallback((index) => {
		setPendingSingleDeleteIndex(index);
	}, []);

	const confirmSingleDelete = useCallback(() => {
		setDates((prev) => {
			const next = [...prev];
			next.splice(pendingSingleDeleteIndex, 1);
			return next;
		});
		setPendingSingleDeleteIndex(null);
	}, [pendingSingleDeleteIndex]);

	const cancelSingleDelete = useCallback(() => {
		setPendingSingleDeleteIndex(null);
	}, []);

	const availableActions = BULK_ACTIONS.filter(
		(a) => selectedCount >= a.requiresMinSelection
	);

	const updateColors = useCallback((values) => {
		setDates((prev) =>
			prev.map((d) =>
				d.selected
					? { ...d, colors: { ...d.colors, ...values } }
					: d
			)
		);
	}, []);

	const handleBulkConfirm = useCallback((payload) => {
		switch (activeBulkAction.id) {
			case "delete":
				setDates((prev) => prev.filter((d) => !d.selected));
				break;

			case "updateColors":
				updateColors(payload);
				break;

			case "combineRanges":
				// 1. Only groups that are actually merged
				const mergableGroups = payload.filter(g => g.length > 1);

				// 2. Map first item id → group
				const groupByFirstId = new Map(
					mergableGroups.map(g => [g[0].id, g])
				);

				// 3. All ids that will be removed (merged away)
				const mergedItemIds = new Set(
					mergableGroups.flatMap(g => g.map(i => i.id))
				);

				setDates(prev => {
					return prev.flatMap(item => {
						// Item not involved in any merge → keep it
						if (!mergedItemIds.has(item.id)) {
							return item;
						}

						// Item is part of a merged group
						const group = groupByFirstId.get(item.id);

						// Only the first item emits the merged row
						if (!group) {
							return [];
						}

						return mergeGroup(group);
					});
				});

				break;

			case "sort":
				setDates((prev) => sortDates(prev, payload));
				break;

			default:
				break;
		}

		setActiveBulkAction(null);
	}, [activeBulkAction, updateColors]);

	return (
		<div className="fixed z-50">
			<div className="fixed z-40 w-screen h-screen inset-0 bg-gray-900 bg-opacity-60"></div>

			<div className="fixed z-50 w-1/2 bg-white rounded-md px-8 py-6 space-y-5 drop-shadow-lg">
				<div className="flex justify-between items-center">
					<h1 className="text-2xl font-semibold">Highlighted Dates</h1>
					<div>
						<Button variant="secondary" label="Import Holidays ..." onClick={onImportHolidaysDates} className="mx-3" />
						<Button variant="secondary" label="Import ..." onClick={onImportDates} className="mx-3" />
						<Button variant="secondary" label="Export ..." onClick={onExportDates} className="mx-3" />
					</div>
				</div>

				<div style={{ minHeight: "200px", maxHeight: "70vh", overflowY: "scroll" }} className="py-5 border-t border-b border-gray-300">
					<table className="table-auto w-full">
						<thead className="border-b-2 border-gray-200">
							<tr>
								<th className="w-10 text-center">
									<input
										type="checkbox"
										ref={selectAllRef}
										checked={dates.length > 0 && dates.every((d) => d.selected)}
										onChange={toggleSelectAll}
									/>
								</th>
								<th className="customDatePickerWidth text-left">Start</th>
								<th className="customDatePickerWidth text-left">End</th>
								<th className="text-left">Note</th>
								<th className="w-48 text-left">Background</th>
								<th className="w-48 text-left">Border</th>
								<th className="w-48 text-left">Text</th>
								<th className="w-10"></th>
							</tr>
						</thead>
						<tbody>
							{dates.map((date, index) => (
								<tr key={date.id} className={index % 2 === 1 ? "bg-gray-200" : "bg-white"}>
									<td className="text-center">
										<input
											type="checkbox"
											checked={date.selected}
											onChange={() => toggleRowSelected(index)}
										/>
									</td>
									<td className="customDatePickerWidth">
										<DatePicker selected={date.startDate} onChange={(d) => updateStartDate(index, d)} />
									</td>
									<td className="customDatePickerWidth">
										<DatePicker selected={date.endDate} onChange={(d) => updateEndDate(index, d)} />
									</td>
									<td className="pr-6">
										<input
											type="text"
											value={date.note}
											onChange={(e) => updateNote(index, e.target.value)}
											className="w-full border rounded p-2 my-1"
										/>
									</td>
									<td><ColorSelector colors={colors} selectedColor={date.colors.background} onSelect={(c) => updateSelectedColor(index, "background", c)} /></td>
									<td><ColorSelector colors={colors} selectedColor={date.colors.border} onSelect={(c) => updateSelectedColor(index, "border", c)} /></td>
									<td><ColorSelector colors={colors} selectedColor={date.colors.text} onSelect={(c) => updateSelectedColor(index, "text", c)} /></td>
									<td className="text-center">
										<div className="ml-auto">
											<DeleteIconButton
												onClick={() => requestSingleDelete(index)}
												title="Remove sort option"
											/>
										</div>
									</td>
								</tr>
							))}
						</tbody>
					</table>

					<div className="mt-3">
						<AddIconButton onClick={addDate} />
						
						<div ref={addButtonWrapperRef}></div>
					</div>
				</div>

				<div className="flex justify-between items-baseline gap-2">
					{selectedCount <= 1 && <div className="text-xs text-gray-600">Select at least 2 items for bulk actions</div>}
					{selectedCount > 1 && <BulkActionDropdown actions={availableActions} disabled={selectedCount <= 1}
						onSelect={(id) =>
							setActiveBulkAction(
								BULK_ACTIONS.find((a) => a.id === id)
							)
						}
					/>}

					<div>
						<Button label="Close" onClick={saveAndClose} variant="secondary" />
					</div>
				</div>

				<BulkActionModalHost
					activeAction={activeBulkAction}
					onConfirm={handleBulkConfirm}
					onClose={() => setActiveBulkAction(null)}
					colors={colors}
					colorOptions={colorOptions}
					dates={dates}
				/>
				{pendingSingleDeleteIndex !== null && (
					<ModalPortal>
						<ConfirmModal
							title="Delete item"
							message="Are you sure you want to delete this entry?"
							onConfirm={confirmSingleDelete}
							onCancel={cancelSingleDelete}
						/>
					</ModalPortal>
				)}
			</div>
		</div>
	);
};

export default SelectDates;